import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { AuthenticatedImage } from "@/components/AuthenticatedImage";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ReviewQueueItem {
  jobId: string;
  fileName: string;
  status: string;
  reviewStatus?: string | null;
  reviewClaimedByUserEmail?: string | null;
  reviewClaimExpiresAtUtc?: string | null;
  secondsUntilClaimExpiry?: number | null;
  isClaimedByCurrentUser: boolean;
}

interface EditableField {
  fieldKey: string;
  value: string;
}

interface ReviewJobDetail {
  jobId: string;
  fileName: string;
  status: string;
  reviewStatus?: string | null;
  ediContent?: string | null;
  displayUrls: string[];
  thumbnailUrls: string[];
  editableFields: EditableField[];
}

const POLL_MS = 1000;

function formatCountdown(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) {
    return "expired";
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

export default function Review() {
  const { toast } = useToast();
  const [queue, setQueue] = useState<ReviewQueueItem[]>([]);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReviewJobDetail | null>(null);
  const [fieldEdits, setFieldEdits] = useState<Record<string, string>>({});
  const [imageIndex, setImageIndex] = useState(0);
  const [imageAnimClass, setImageAnimClass] = useState("");
  const [reprocessOpen, setReprocessOpen] = useState(false);
  const [reprocessText, setReprocessText] = useState("");

  const loadQueue = useCallback(async () => {
    const response = await api.getReviewQueue();
    if (response?.success && Array.isArray(response.data)) {
      setQueue(response.data as ReviewQueueItem[]);
    }
  }, []);

  useEffect(() => {
    loadQueue();
    const timer = window.setInterval(loadQueue, POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadQueue]);

  const loadDetail = async (jobId: string) => {
    const response = await api.getReviewJob(jobId);
    if (!response?.success || !response.data) {
      toast({
        title: "Error",
        description: response?.message || "Could not load job",
        variant: "destructive",
      });
      return;
    }
    const data = response.data as ReviewJobDetail;
    setDetail(data);
    setImageIndex(0);
    const initial: Record<string, string> = {};
    (data.editableFields || []).forEach((f) => {
      initial[f.fieldKey] = f.value ?? "";
    });
    setFieldEdits(initial);
  };

  const openJob = async (item: ReviewQueueItem) => {
    if (item.status === "ReadyForReview" && !item.isClaimedByCurrentUser) {
      const claim = await api.claimReviewJob(item.jobId);
      if (!claim.success) {
        toast({
          title: "Could not claim",
          description: claim.message || "Job may be claimed by someone else.",
          variant: "destructive",
        });
        await loadQueue();
        return;
      }
    }
    setActiveJobId(item.jobId);
    await loadDetail(item.jobId);
    await loadQueue();
  };

  const closeDetail = () => {
    setActiveJobId(null);
    setDetail(null);
    setFieldEdits({});
    setReprocessOpen(false);
    setReprocessText("");
    loadQueue();
  };

  const goToImage = (targetIndex: number) => {
    if (!detail?.displayUrls?.length) {
      return;
    }
    const n = detail.displayUrls.length;
    const target = ((targetIndex % n) + n) % n;
    if (target === imageIndex) {
      return;
    }
    const diffForward = (target - imageIndex + n) % n;
    const forward = diffForward <= n / 2;
    const outClass = forward ? "-translate-x-full opacity-0" : "translate-x-full opacity-0";
    setImageAnimClass(outClass);
    window.setTimeout(() => {
      setImageIndex(target);
      setImageAnimClass(forward ? "translate-x-full opacity-0" : "-translate-x-full opacity-0");
      window.requestAnimationFrame(() => {
        setImageAnimClass("translate-x-0 opacity-100");
      });
    }, 280);
  };

  const changeImage = (delta: number) => {
    goToImage(imageIndex + delta);
  };

  useEffect(() => {
    if (detail?.displayUrls?.length) {
      setImageAnimClass("translate-x-0 opacity-100");
    }
  }, [detail?.jobId]);

  const handleAccept = async () => {
    if (!activeJobId) {
      return;
    }
    const response = await api.acceptReviewJob(activeJobId, fieldEdits);
    if (response.success) {
      toast({ title: "Accepted", description: "EDI generated and sent." });
      closeDetail();
    } else {
      toast({
        title: "Error",
        description: response.message || "Accept failed",
        variant: "destructive",
      });
    }
  };

  const handleUnclaim = async () => {
    if (!activeJobId) {
      return;
    }
    const response = await api.unclaimReviewJob(activeJobId);
    if (response.success) {
      toast({ title: "Unclaimed", description: "Job is available for others." });
      closeDetail();
    } else {
      toast({
        title: "Error",
        description: response.message || "Unclaim failed",
        variant: "destructive",
      });
    }
  };

  const submitReprocess = async () => {
    if (!activeJobId || !reprocessText.trim()) {
      return;
    }
    const response = await api.reprocessReviewJob(activeJobId, reprocessText.trim());
    if (response.success) {
      toast({ title: "Submitted", description: "Reprocess started." });
      closeDetail();
    } else {
      toast({
        title: "Error",
        description: response.message || "Reprocess failed",
        variant: "destructive",
      });
    }
    setReprocessOpen(false);
    setReprocessText("");
  };

  if (activeJobId && detail) {
    const displayList = detail.displayUrls?.length
      ? detail.displayUrls
      : detail.thumbnailUrls || [];
    const thumbList = detail.thumbnailUrls?.length
      ? detail.thumbnailUrls
      : detail.displayUrls || [];

    return (
      <div className="p-4 h-[calc(100vh-4rem)] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold truncate pr-4">{detail.fileName}</h1>
          <Button variant="outline" size="sm" onClick={closeDetail}>
            Back to list
          </Button>
        </div>
        <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
          <div className="flex-1 flex flex-col min-h-0 border rounded-lg p-2 bg-muted/30">
            <div className="relative flex-1 min-h-[240px] flex items-center justify-center overflow-hidden bg-background rounded">
              {displayList.length > 0 ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute left-1 z-10"
                    onClick={() => changeImage(-1)}
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </Button>
                  <div
                    className={cn(
                      "max-w-full max-h-full flex items-center justify-center transition-all duration-300 ease-out",
                      imageAnimClass
                    )}
                  >
                    <AuthenticatedImage
                      src={displayList[imageIndex]}
                      alt={`Page ${imageIndex + 1}`}
                      className="max-h-[min(55vh,520px)] max-w-full object-contain"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 z-10"
                    onClick={() => changeImage(1)}
                    aria-label="Next image"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </>
              ) : (
                <span className="text-muted-foreground">No images</span>
              )}
            </div>
            <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
              {thumbList.map((url, idx) => (
                <button
                  type="button"
                  key={idx}
                  className={cn(
                    "shrink-0 rounded border-2 transition-colors",
                    idx === imageIndex ? "border-primary" : "border-transparent opacity-70"
                  )}
                  onClick={() => {
                    goToImage(idx);
                  }}
                >
                  <AuthenticatedImage
                    src={url}
                    alt=""
                    className="h-16 w-16 object-cover rounded"
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 flex flex-col min-h-0 border rounded-lg p-3">
            <Tabs defaultValue="raw" className="flex-1 flex flex-col min-h-0">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="raw">Raw EDI</TabsTrigger>
                <TabsTrigger value="fields">Fields</TabsTrigger>
              </TabsList>
              <TabsContent value="raw" className="flex-1 min-h-0 mt-2">
                <textarea
                  readOnly
                  className="w-full h-[min(50vh,400px)] text-xs font-mono border rounded p-2 bg-muted/50 resize-none"
                  value={detail.ediContent || ""}
                />
              </TabsContent>
              <TabsContent value="fields" className="flex-1 min-h-0 mt-2 overflow-y-auto">
                <div className="space-y-3 pr-1">
                  {(detail.editableFields || []).map((f) => (
                    <div key={f.fieldKey}>
                      <Label className="text-xs text-muted-foreground">{f.fieldKey}</Label>
                      <Input
                        value={fieldEdits[f.fieldKey] ?? ""}
                        onChange={(e) =>
                          setFieldEdits((prev) => ({
                            ...prev,
                            [f.fieldKey]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
              <Button variant="outline" onClick={handleUnclaim}>
                Unclaim
              </Button>
              <Button onClick={handleAccept}>Accept</Button>
              <Button variant="secondary" onClick={() => setReprocessOpen(true)}>
                Reprocess with instructions
              </Button>
            </div>
          </div>
        </div>
        {reprocessOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-lg shadow-lg p-6 max-w-lg w-full space-y-4">
              <h2 className="text-lg font-semibold">Reprocess instructions</h2>
              <textarea
                className="w-full min-h-[120px] border rounded p-2 text-sm"
                value={reprocessText}
                onChange={(e) => setReprocessText(e.target.value)}
                placeholder="Describe what to fix (re-extract, placement hints, etc.)"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReprocessOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={submitReprocess} disabled={!reprocessText.trim()}>
                  Submit
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Review queue</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Refreshes every second. Claim a document to review; only one active claim per user.
      </p>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-2">File</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Claim</th>
              <th className="text-left p-2">Expires</th>
              <th className="text-right p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {queue.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-muted-foreground">
                  No documents waiting for review.
                </td>
              </tr>
            ) : (
              queue.map((row) => (
                <tr key={row.jobId} className="border-t">
                  <td className="p-2">{row.fileName}</td>
                  <td className="p-2">{row.status}</td>
                  <td className="p-2">
                    {row.reviewClaimedByUserEmail ? (
                      <span title={row.reviewClaimedByUserEmail}>
                        {row.reviewClaimedByUserEmail}
                        {row.isClaimedByCurrentUser ? " (you)" : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-2">
                    {row.secondsUntilClaimExpiry != null
                      ? formatCountdown(row.secondsUntilClaimExpiry)
                      : "—"}
                  </td>
                  <td className="p-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openJob(row)}
                      disabled={
                        row.status === "InReview" &&
                        !row.isClaimedByCurrentUser
                      }
                    >
                      {row.isClaimedByCurrentUser
                        ? "Continue"
                        : row.status === "ReadyForReview"
                        ? "Claim & open"
                        : "View"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
